import React, { useState, useMemo, useEffect } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { Box, Typography, Tabs, Tab, Grid, Stack, TextField, InputAdornment, MenuItem, Select, TablePagination } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import TaskProjectCard from '../../../components/ProjectCard/TaskProjectCard'
import TaskTabToggle from '../../../components/TabToggle/TaskTabToggle'
import AdxHeaderButton from 'components/reusable/AdxHeaderButton/AdxHeaderButton'
import { format } from 'date-fns'
import CreateTaskModal from '../../../components/CreateTaskModal'
import ToReviewTab from '../../../components/ToReviewTab'
import { CARDS_PER_PAGE } from 'module/task-management/constants'
import MyReviewsTab from '../../../components/MyReviewsTab'
import WorkPlanner from '../../../components/WorkPlanner/WorkPlanner';
import CreateProjectModal from '../../project-tracker/view/CreateProjectModal';
import { useMyAccount, useGetMyTasks, useGetMyTeamMemberProjects, useGetToReviews, useGetProjectTrackerStaff } from 'services/api/task-management/trackerHooks';
import TabBadge from '../../../components/TabBadge';
import { TASK_STATUS } from '../../../helper/taskStatuses';

// Helper function to get current date in "Month Day, Year" format
function getCurrentDate() {
  const today = new Date();
  return format(today, 'EEE, dd MMMM yyyy');
}

const TASK_DISPLAY_PRIORITY = {
  [TASK_STATUS.ONGOING]: 0,
  [TASK_STATUS.RETURNED_FOR_REWORK]: 1,
  [TASK_STATUS.PENDING]: 2,
  [TASK_STATUS.SUBMITTED_FOR_REVIEW]: 3,
  [TASK_STATUS.COMPLETED]: 4,
}

const getTaskSortPriority = (status) =>
  TASK_DISPLAY_PRIORITY[status] ?? Number.MAX_SAFE_INTEGER

// Move TabPanel outside component to prevent re-renders on every keystroke
const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

const MyTasks = () => {

  // Success and error message state
  const [successMessage, setSuccessMessage] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  const {
    data: accountData,
    isLoading: isLoadingAccount,
    isError: isAccountError
  } = useMyAccount()

  // Fetch projects where current user is a team member
  // Excludes projects where user is ONLY manager
  const {
    data: projectsData,
    isLoading: isLoadingProjects
  } = useGetMyTeamMemberProjects()

  // Fetch my tasks (only tasks assigned to current user)
  const {
    data: tasksData,
    isLoading: isLoadingTasks
  } = useGetMyTasks()

  // Extract user info with correct path
  // const userName = accountData?.data?.staff?.name || 'User'
  // const userEmail = accountData?.data?.staff?.work_email || ''
  const userAvatar = accountData?.data?.profile_image_url || ''
  // const isSuperAdmin = accountData?.data?.super_admin || false

  // Extract projects array from API response for CreateTaskModal
  // The projects array is nested at projectsData.data.projects
  const availableProjects = Array.isArray(projectsData?.data?.projects)
    ? projectsData.data.projects
    : Array.isArray(projectsData?.data)
      ? projectsData.data
      : Array.isArray(projectsData)
        ? projectsData
        : []

  // Extract tasks array from API response
  const availableTasks = useMemo(() =>
    Array.isArray(tasksData?.data?.tasks) ? tasksData.data.tasks : [],
    [tasksData]
  )

  console.log('raw project sample:', availableProjects[0])

  const transformedProjects = useMemo(() => {
    if (!availableProjects || availableProjects.length === 0) return []

    return availableProjects.map(project => {
      // Get creator info from project data
      let creatorName = 'Unknown Creator'
      let creatorAvatar = null

      // API returns created_by as an ID — match against members array
      if (project.members && Array.isArray(project.members)) {
        const creatorMember = project.members.find(m =>
          String(m.staff?.id) === String(project.created_by)
        )
        if (creatorMember?.staff) {
          if (String(accountData?.data?.staff?.id) === String(creatorMember.staff.id)) {
            creatorName = 'You'
            creatorAvatar = userAvatar
          } else {
            creatorName = creatorMember.staff.name
            creatorAvatar = creatorMember.staff.profile_image_url || null
          }
        }
      }

      // Filter tasks for this project
      const projectTasks = availableTasks.filter(task => task.project_id === project.id)

      // Calculate task stats (still used for counts/list)
      const total = projectTasks.length
      const completed = projectTasks.filter(t => t.status === 'completed').length
      const ongoing = projectTasks.filter(t => t.status === 'ongoing').length
      const pending = projectTasks.filter(t => t.status === 'pending').length

      // Deliverable-based progress from BE
      const deliverableCompleted = project?.user_deliverable_stats?.completed_value ?? 0
      const deliverableTotal = project?.user_deliverable_stats?.total_value ?? 0
      const deliverableProgress =
        deliverableTotal > 0 ? Math.round((deliverableCompleted / deliverableTotal) * 100) : 0

      // Transform tasks to UI format
      const tasks = projectTasks
        .map(task => ({
          id: task.id,
          name: task.name,
          status: task.status,
          value: parseFloat(task.value),
          due_date: task.due_date,
          scheduled_date: task.scheduled_date,
          assigned_to: task.assigned_to?.name,
          created_at: task.created_at
        }))
        .sort((a, b) => {
          const priorityDiff = getTaskSortPriority(a.status) - getTaskSortPriority(b.status)
          if (priorityDiff !== 0) return priorityDiff

          const aCreatedAt = new Date(a.created_at || 0).getTime()
          const bCreatedAt = new Date(b.created_at || 0).getTime()
          return bCreatedAt - aCreatedAt
        })

      // Calculate days left based on project deadline
      const deadline = project.deadline ? new Date(project.deadline) : new Date()
      const today = new Date()
      const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24))

      // Determine status
      let status = 'on-track'
      if (daysLeft < 0) status = 'overdue'
      else if (daysLeft <= 7) status = 'at-risk'

      return {
        id: project.id,
        creator: creatorName,
        avatar: creatorAvatar,
        title: project.name,
        objective: project.objective,
        description: project.background,
        // related_links is an array — extract the first entry
        link: Array.isArray(project.related_links) && project.related_links.length > 0
          ? project.related_links[0]
          : null,
        tasks: tasks,
        completed: completed,
        total: total,

        // progress used by bar is deliverables-based now
        progress: deliverableProgress,

        completedCount: completed,
        pendingCount: pending,
        ongoingCount: ongoing,
        dueDate: project.deadline ? format(new Date(project.deadline), 'dd MMMM yyyy') : 'No deadline',
        daysLeft: Math.abs(daysLeft),
        status: status,

        // keep BE stats for label
        user_deliverable_stats: project.user_deliverable_stats
      }
    })
  }, [availableProjects, availableTasks, accountData, userAvatar])

  const [activeMainTab, setActiveMainTab] = useState(0) // 0: To Do, 1: To Review, 2: My Reviews
  const [activeSubTab, setActiveSubTab] = useState('tasks') // 'tasks' or 'work-planner'

  // Get URL search params to determine which tab to open
  const [searchParams] = useSearchParams()
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam !== null) {
      setActiveMainTab(parseInt(tabParam, 10))
      // Remove query parameter from URL after setting the tab
      // This prevents redirect from persisting on page refresh
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [searchParams])

  useEffect(() => {
    if (!successMessage) return
    const t = setTimeout(() => setSuccessMessage(null), 5000)
    return () => clearTimeout(t)
  }, [successMessage])

  useEffect(() => {
    if (!errorMessage) return
    const t = setTimeout(() => setErrorMessage(null), 5000)
    return () => clearTimeout(t)
  }, [errorMessage])

  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false)
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false)

  const [page, setPage] = useState(0)
  const cardsPerPage = CARDS_PER_PAGE

  const navigate = useNavigate()
  const routerLocation = useLocation()

  useEffect(() => {
    if (routerLocation.state?.flash?.type === 'viewCompleted') {   // fix 2 & 3: use routerLocation
      setActiveMainTab(2)   // switch to My Reviews tab (index 2)
      navigate(routerLocation.pathname, { replace: true, state: {} })
    }
  }, [routerLocation.state]) 

  // notifications tab part
  // calculate to do badge count (task with status "pendind_tasks" and "ongoing_tasks")
  const todoBadgeCount = useMemo(() => {
    return availableTasks.filter(task =>
      task.status === 'ongoing' || task.status === 'pending' || task.status === 'returned_for_rework'
    ).length
  }, [availableTasks])

  // get review data and badge count
  const { data: toReviewData } = useGetToReviews()
  const toReviewBadgeCount = useMemo(() => {
    return Array.isArray(toReviewData?.data?.to_review) ? toReviewData.data.to_review.length : 0
  }, [toReviewData])

  // MyReviews pagination state
  const [myReviewsPagination, setMyReviewsPagination] = useState({
    page: 0,
    rowsPerPage: 6,
    totalCount: 0,
    onPageChange: null
  })

  // ToReview pagination state (provided by ToReviewTab)
  const [toReviewPagination, setToReviewPagination] = useState({
    page: 0,
    rowsPerPage: CARDS_PER_PAGE,
    totalCount: 0,
    onPageChange: null,
    activeTab: 'pending'
  })

  // Filter states - Fixed initialization
  const [searchProject, setSearchProject] = useState('');
  const [searchManager, setSearchManager] = useState('all')
  const [sortDate, setSortDate] = useState('nearest')

  // Use transformed staging data instead of mock data
  const projects = transformedProjects

  const [currentDate] = useState(getCurrentDate())

  const handleMainTabChange = (event, newValue) => {
    setActiveMainTab(newValue)
    setPage(0) // reset to first page on pagination
  }

  const handleSubTabChange = (newTab) => {
    setActiveSubTab(newTab)
    setPage(0) // reset to first page on pagination
  }

  const handleTaskUpdate = (action, data) => {
    switch (action) {
      case 'submit':
        console.log('Task submitted for review:', data)
        break

      case 'edit':
        console.log('Edit task:', data)
        break

      case 'delete':
        console.log('Delete Task:', data)
        break

      default:
        break
    }
  }

  const handleAddTask = (projectId) => {
    console.log('Add task to project:', projectId)
    setIsCreateTaskModalOpen(true);
  }

  const handleViewCompleted = (projectId) => {
    console.log('View completed tasks for project:', projectId)
  }

  const handleCreateTasks = () => {
    console.log('Create new tasks')
    setIsCreateTaskModalOpen(true);
  }

  const handleCloseModal = () => {
    setIsCreateTaskModalOpen(false);
    setIsCreateProjectModalOpen(false);
  }

  const handleCreateProject = () => {
    console.log('Create new project')
    setIsCreateProjectModalOpen(true);
  }

  const handlePageChange = (event, newPage) => {
    setPage(newPage)
  }

  // Group projects by manager with useMemo
  // const groupedProjectsArray = useMemo(() => {
  //   const grouped = projects.reduce((acc, project) => {
  //     const creator = project.creator
  //     if (!acc[creator]) {
  //       acc[creator] = {
  //         creator,
  //         avatar: project.avatar,
  //         projects: []
  //       }
  //     }
  //     acc[creator].projects.push(project)
  //     return acc
  //   }, {})
  //   return Object.values(grouped)
  // }, [projects])

  const groupedProjectsArray = useMemo(() => {
    const grouped = projects.reduce((acc, project) => {
      const creator = project.creator
      if (!acc[creator]) {
        acc[creator] = {
          creator,
          avatar: project.avatar,
          projects: []
        }
      }
      acc[creator].projects.push(project)
      return acc
    }, {})

    const allGroups = Object.values(grouped)

    // separate "You" group from others
    const meGroup = allGroups.find(g => g.creator === 'You')
    const otherGroups = allGroups
      .filter(g => g.creator !== 'You')
      .sort((a, b) => {
        // push "Unknown Creator" to the end
        if (a.creator === 'Unknown Creator') return 1
        if (b.creator === 'Unknown Creator') return -1
        return a.creator.localeCompare(b.creator)
      })

      // always shows "You" first - even if no projects assigned yet
      const meGroupOrEmpty = meGroup ?? {
        creator: 'You',
        avatar: userAvatar,
        projects: []
      }

      // "You" always first, then oter project manager in alphabetical order
      return [meGroupOrEmpty, ...otherGroups]
    }, [projects, userAvatar])

  // Get unique managers for dropdown filter
  const uniqueManagers = useMemo(() => {
    return [...new Set(projects.map(p => p.creator))].filter(Boolean)
  }, [projects])

  // Filter and sort managers
  const filteredManagers = useMemo(() => {
    let filtered = [...groupedProjectsArray]

    // Filter by search — match project title OR any task name
    if (searchProject && searchProject.trim() !== '') {
      const query = searchProject.toLowerCase()

      filtered = filtered.map(group => ({
        ...group,
        projects: group.projects.filter(p => {
          // Check if project title matches
          const projectMatches = p.title.toLowerCase().includes(query)

          // Check if any task name matches
          const taskMatches = Array.isArray(p.tasks) && p.tasks.some(task =>
            task.name?.toLowerCase().includes(query)
          )

          return projectMatches || taskMatches
        })
      })).filter(group => group.projects.length > 0)
    }

    // Filter by creator
    if (searchManager !== 'all') {
      filtered = filtered.filter(group => group.creator === searchManager)
    }

    // Sort by date
    if (sortDate === 'nearest') {
      filtered = filtered.map(group => ({
        ...group,
        projects: [...group.projects].sort((a, b) => a.daysLeft - b.daysLeft)
      }))
    }

    return filtered
  }, [groupedProjectsArray, searchProject, searchManager, sortDate])

  // Paginate managers
  const paginatedManagers = useMemo(() => {
    const startIndex = page * cardsPerPage
    return filteredManagers.slice(startIndex, startIndex + cardsPerPage)
  }, [filteredManagers, page])

  return (
    <>
      {/* Success Message Banner */}
      {successMessage && (
        <Box sx={{
          position: 'fixed',
          top: 24,
          left: '70%',
          transform: 'translateX(-70%)',
          zIndex: 9999,
          width: '100%',
          maxWidth: 600,
          px: 2
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            backgroundColor: '#F0FAF4',
            borderLeft: '4px solid #4CAF50',
            borderRadius: '4px',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
            py: 1.5,
            px: 2
          }}>
            <Box sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: '#28A745',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg width="12" height="9" viewBox="0 0 12 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.5 6.79L1.71 4L0.75 4.95L4.5 8.7L11.25 1.95L10.295 1L4.5 6.79Z" fill="white" />
              </svg>
            </Box>
            <Typography component="div" sx={{ fontSize: '14px', color: '#1B5E20', fontWeight: 400 }}>
              {successMessage}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Error Message Banner - fixed top center, outside everything */}
      {errorMessage && (
        <Box sx={{
          position: 'fixed',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          width: '100%',
          maxWidth: 600,
          px: 2
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            backgroundColor: '#FFF0F0',
            borderLeft: '4px solid #EF5350',
            borderRadius: '4px',
            boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
            py: 1.5,
            px: 2
          }}>
            <Box sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: '#DC3545',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0ZM6.75 9H5.25V7.5H6.75V9ZM6.75 6H5.25V3H6.75V6Z" fill="white" />
              </svg>
            </Box>
            <Typography sx={{ fontSize: '14px', color: '#7A1F1F', fontWeight: 400 }}>
              {errorMessage}
            </Typography>
          </Box>
        </Box>
      )}

      <DashboardContainer headerTitle="My Tasks" noPadding={true} sx={{
        p: 0,
        m: 0,
        height: '100%',
        overflow: 'hidden'
      }}>
        <Box sx={{ px: 0, py: 0 }}>

          <CreateTaskModal
            open={isCreateTaskModalOpen}
            onClose={handleCloseModal}
            projects={availableProjects}
            setSuccessMessage={setSuccessMessage}
            setErrorMessage={setErrorMessage}
          />

          <CreateProjectModal
            open={isCreateProjectModalOpen}
            onClose={handleCloseModal}
            onProjectCreated={(createdProject) => {
              // close modal
              setIsCreateProjectModalOpen(false)

              // redirect user to Project Tracker with flash data
              navigate('/app/tasks-dashboard/project-tracker', {
                state: {
                  flash: {
                    type: 'projectCreated',
                    name: createdProject?.name || '',
                  },
                },
              })
            }}
          />

          {/* Main Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              pr: 2
            }}>
              <Tabs
                value={activeMainTab}
                onChange={handleMainTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: '#2563eb',
                      fontWeight: 600,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#2563eb',
                  },
                }}
              >
                <Tab label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    To Do
                    <TabBadge count={todoBadgeCount} />
                  </Box>
                } />
                <Tab label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    To Review
                    <TabBadge count={toReviewBadgeCount} />
                  </Box>
                } />
                <Tab label="My Reviews" />
              </Tabs>

              {/* Buttons moved inside same row as tabs */}
              <Box display="flex" gap={2} alignItems="center">
                <AdxHeaderButton
                  icon={<AddIcon />}
                  onClick={handleCreateTasks}
                  color="primaryTask"
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 400 }}>
                    Create tasks
                  </Typography>
                </AdxHeaderButton>

                <AdxHeaderButton
                  icon={<AddIcon />}
                  onClick={handleCreateProject}
                  color="secondaryTask"
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                    Create project
                  </Typography>
                </AdxHeaderButton>
              </Box>
            </Box>
          </Box>

          {/* Tab Content */}
          <TabPanel value={activeMainTab} index={0}>

            {/* Task/Work Planner Toggle */}
            <TaskTabToggle activeTab={activeSubTab} onTabChange={handleSubTabChange} />

            {/* Conditional header based on active tab */}
            {activeSubTab === 'tasks' ? (
              <Box>

                <Typography variant="h3" fontWeight={500} color="#000000">
                  To Do
                </Typography>
                <Typography variant="caption" color="#000000">
                  Prioritize and manage your tasks.
                </Typography>

                {/* Search and filters part */}
                <Box
                  sx={{
                    mt: 2,
                    mb: 4,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'center' },
                    justifyContent: { xs: 'flex-start', md: 'space-between' },
                    gap: 2, // spacing between rows/columns
                  }}
                >
                  {/* Search filter pill */}
                  <Box
                    sx={{
                      width: { xs: '100%', md: 400, lg: 500}, // full width on mobile, fixed on desktop
                      maxWidth: '100%',
                      flexGrow: { xs: 1, md: 0 },
                    }}
                  >
                    <TextField
                      placeholder="Search a projects or tasks"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={searchProject}
                      onChange={(e) => setSearchProject(e.target.value)}
                      sx={{
                        outline: 'none !important',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '50px',
                          backgroundColor: '#f9f9f9',
                          outline: 'none !important',
                          '& fieldset': { borderColor: '#8A8A8A !important', outline: 'none !important' },
                          '&:hover fieldset': { borderColor: '#8A8A8A !important', outline: 'none !important' },
                          '&.Mui-focused fieldset': {
                            borderColor: '#8A8A8A !important',
                            borderWidth: '1px !important',
                            outline: 'none !important',
                          },
                          '&.Mui-focused': { boxShadow: 'none !important', outline: 'none !important' },
                          '&:focus-visible': { outline: 'none !important' },
                        },
                        '& input': {
                          outline: 'none !important',
                          '&:focus': { outline: 'none !important', boxShadow: 'none !important' },
                          '&:focus-visible': { outline: 'none !important' },
                        },
                        '&:focus-within': { outline: 'none !important' },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Filters container */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' }, // stack on xs, row on sm+
                      gap: 2,
                      width: { xs: '100%', md: 'auto' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                      flexWrap: 'wrap', // allow wrapping if tight
                    }}
                  >
                    {/* 2. Manager Filter */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                      }}
                    >
                      <FilterAltOutlinedIcon sx={{ color: '#999', flexShrink: 0 }} />
                      <Select
                        value={searchManager}
                        onChange={(e) => setSearchManager(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                          borderRadius: '50px',
                          width: { xs: '100%', sm: 220 }, // full on mobile, pill on desktop
                          backgroundColor: '#f9f9f9',
                          outline: 'none !important',
                          '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center',
                            outline: 'none !important',
                            '&:focus': { outline: 'none !important', backgroundColor: '#f9f9f9' },
                          },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#8A8A8A !important' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#8A8A8A !important' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#8A8A8A !important',
                            borderWidth: '1px !important',
                          },
                          '&.Mui-focused': { boxShadow: 'none !important' },
                        }}
                        renderValue={(value) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                            <PersonOutlineIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                            <Box
                              component="span"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {value === 'all' ? 'All creators' : value}
                            </Box>
                          </Box>
                        )}
                      >
                        <MenuItem value="all">All managers</MenuItem>
                        {uniqueManagers.map((manager) => (
                          <MenuItem key={manager} value={manager}>
                            {manager}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>

                    {/* 3. Sorting Filter */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        flex: { xs: '1 1 100%', sm: '0 1 auto' },
                      }}
                    >
                      <SwapVertIcon sx={{ color: '#999', flexShrink: 0 }} />
                      <Select
                        value={sortDate}
                        onChange={(e) => setSortDate(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                          borderRadius: '50px',
                          width: { xs: '100%', sm: 220 },
                          backgroundColor: '#f9f9f9',
                          outline: 'none !important',
                          '& .MuiSelect-select': {
                            outline: 'none !important',
                            '&:focus': { outline: 'none !important', backgroundColor: '#f9f9f9' },
                          },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#8A8A8A !important' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#8A8A8A !important' },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#8A8A8A !important',
                            borderWidth: '1px !important',
                          },
                          '&.Mui-focused': { boxShadow: 'none !important' },
                        }}
                        renderValue={(value) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarMonthIcon sx={{ fontSize: 20 }} />
                            {value === 'nearest' ? 'Nearest Due' : 'Latest Added'}
                          </Box>
                        )}
                      >
                        <MenuItem value="nearest">Nearest Due</MenuItem>
                        <MenuItem value="latest">Latest Added</MenuItem>
                      </Select>
                    </Stack>
                  </Box>
                </Box>

                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    mb: 2,
                    color: '#3a3a3a'
                  }}
                >
                  Project Manager
                </Typography>
              </Box>

            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2 }}>

                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px'
                  }}
                >
                  Weekly
                </Typography>

                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#000000'
                  }}
                >
                  Today is {currentDate}
                </Typography>

              </Box>
            )}

            {/* Projects Grid with Fixed Height */}
            {activeSubTab === 'tasks' ? (
              <Box
                sx={{
                  height: 'calc(100vh - 80px)', // Adjust based on header/filter height
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden' // Prevent outer scroll
                }}
              >
                {/* Cards Container */}
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  mb: 0,
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#d0d0d0',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: '#b0b0b0'
                    }
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '3px'
                  }
                }}
                >
                  {paginatedManagers.length === 0 ? (
                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" width="100%">

                      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={4}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="74" height="74" viewBox="0 0 74 74" fill="none">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M37.7707 8.73752C36.3523 8.49702 34.416 8.47852 30.9257 8.47852C25.0149 8.47852 20.8123 8.48468 17.6303 8.91018C14.51 9.32643 12.7186 10.1127 11.4143 11.4138C10.1101 12.715 9.32692 14.5064 8.91067 17.6083C8.48517 20.7779 8.479 24.9528 8.479 30.8358V43.1691C8.479 49.0459 8.48517 53.2208 8.91067 56.3904C9.32692 59.4923 10.1101 61.2806 11.4143 62.5879C12.7186 63.8891 14.5069 64.6723 17.6088 65.0885C20.7784 65.5171 24.9533 65.5202 30.8332 65.5202H43.1665C49.0464 65.5202 53.2243 65.514 56.394 65.0885C59.4928 64.6723 61.2811 63.8891 62.5853 62.5849C63.8896 61.2806 64.6728 59.4923 65.089 56.3904C65.5145 53.2238 65.5207 49.0459 65.5207 43.166V41.8186C65.5207 37.0826 65.4898 34.8379 64.9842 33.1452H55.3333C51.8399 33.1452 48.9878 33.1452 46.7308 32.843C44.3783 32.5254 42.3371 31.8409 40.706 30.2098C39.0749 28.5788 38.3904 26.5407 38.0728 24.1819C37.7707 21.9311 37.7707 19.0759 37.7707 15.5794V8.73752ZM42.3957 11.1302V15.416C42.3957 19.116 42.4018 21.6567 42.6578 23.5653C42.9044 25.4091 43.3484 26.3125 43.9774 26.9384C44.6064 27.5644 45.5068 28.0114 47.3506 28.2581C49.2592 28.514 51.7998 28.5202 55.4998 28.5202H61.7282C60.5449 27.3936 59.3422 26.2875 58.1207 25.2025L45.9138 14.2166C44.7623 13.1638 43.5893 12.1348 42.3957 11.1302ZM31.3728 3.85352C35.6432 3.85352 38.4028 3.85352 40.9403 4.82477C43.4779 5.7991 45.5191 7.63677 48.6795 10.4827L49.0094 10.7787L61.2133 21.7646L61.5987 22.1099C65.2493 25.3937 67.6112 27.5181 68.8784 30.3671C70.1457 33.2161 70.1488 36.3919 70.1457 41.3006V43.3387C70.1457 49.0058 70.1457 53.4952 69.6739 57.0071C69.1868 60.6208 68.1631 63.5468 65.8568 65.8563C63.5473 68.1626 60.6213 69.1863 57.0076 69.6734C53.4926 70.1452 49.0063 70.1452 43.3392 70.1452H30.6605C24.9933 70.1452 20.504 70.1452 16.9921 69.6734C13.3784 69.1863 10.4523 68.1626 8.14292 65.8563C5.83659 63.5468 4.81292 60.6208 4.32575 57.0071C3.854 53.4921 3.854 49.0058 3.854 43.3387V30.6631C3.854 24.9959 3.854 20.5066 4.32575 16.9947C4.81292 13.381 5.83659 10.4549 8.14292 8.14552C10.4554 5.8361 13.3877 4.81552 17.0168 4.32835C20.5441 3.8566 25.055 3.8566 30.753 3.8566H31.3758" fill="#8A8A8A" />
                          <path d="M55.5002 44.709C55.5002 47.2651 54.1188 49.334 52.4168 49.334C50.7148 49.334 49.3335 47.2651 49.3335 44.709C49.3335 42.156 50.7148 40.084 52.4168 40.084C54.1188 40.084 55.5002 42.156 55.5002 44.709Z" fill="#8A8A8A" />
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M25.8258 52.6748C26.166 52.1645 26.695 51.8103 27.2963 51.6901C27.8977 51.5698 28.5222 51.6933 29.0325 52.0335C31.3922 53.6058 34.1643 54.4448 36.9998 54.4448C39.8353 54.4448 42.6075 53.6058 44.9671 52.0335C45.2197 51.861 45.504 51.7404 45.8035 51.6786C46.103 51.6168 46.4118 51.615 46.712 51.6733C47.0122 51.7316 47.2979 51.8489 47.5525 52.0184C47.807 52.1879 48.0254 52.4062 48.1951 52.6606C48.3647 52.9151 48.4822 53.2007 48.5407 53.5008C48.5993 53.801 48.5977 54.1098 48.536 54.4093C48.4744 54.7089 48.354 54.9932 48.1817 55.2459C48.0095 55.4986 47.7888 55.7146 47.5325 55.8815C44.4131 57.9602 40.7484 59.0694 36.9998 59.0694C33.2513 59.0694 29.5865 57.9602 26.4671 55.8815C25.9569 55.5413 25.6027 55.0123 25.4824 54.4109C25.3621 53.8096 25.4857 53.1851 25.8258 52.6748Z" fill="#8A8A8A" />
                          <path d="M24.6667 44.709C24.6667 47.2651 23.2853 49.334 21.5833 49.334C19.8813 49.334 18.5 47.2651 18.5 44.709C18.5 42.156 19.8813 40.084 21.5833 40.084C23.2853 40.084 24.6667 42.156 24.6667 44.709Z" fill="#8A8A8A" />
                        </svg>

                        <Typography fontSize="14px" fontWeight={500} color="#8a8a8a">
                          You do not have any tasks yet. Assigned projects and newly created tasks will show up here.
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Grid container spacing={3} alignItems="flex-start">
                      {paginatedManagers.map((group, groupIndex) => (
                        <Grid item xs={12} md={6} lg={4} key={groupIndex}>
                          <TaskProjectCard
                            manager={group.creator}
                            avatar={group.avatar}
                            projects={group.projects}
                            onAddTask={handleAddTask}
                            onViewCompleted={handleViewCompleted}
                            onTaskUpdate={handleTaskUpdate}
                            setSuccessMessage={setSuccessMessage}
                            setErrorMessage={setErrorMessage}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Box>
            ) : (
              <Box>
                <WorkPlanner tasks={availableTasks} />
              </Box>
            )}
          </TabPanel>

          <TabPanel value={activeMainTab} index={1}>
            <ToReviewTab onPaginationChange={setToReviewPagination} />
          </TabPanel>

          <TabPanel value={activeMainTab} index={2}>
            <MyReviewsTab
              onPaginationChange={setMyReviewsPagination}
              setSuccessMessage={setSuccessMessage}
              setErrorMessage={setErrorMessage}
            />
          </TabPanel>
        </Box>
      </DashboardContainer>

      {/* Pagination outside DashboardContainer - To Do Tab */}
      {activeMainTab === 0 && activeSubTab === 'tasks' && filteredManagers.length > 0 && (
        <Box
          sx={{
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: '#ffffff',
            position: 'sticky',
            bottom: 0,
            zIndex: 10
          }}
        >
          <TablePagination
            component="div"
            count={filteredManagers.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={cardsPerPage}
            rowsPerPageOptions={[]}
            showFirstButton={true}
            showLastButton={true}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count}`
            }
            sx={{
              '& .MuiTablePagination-toolbar': {
                minHeight: '48px',
                paddingLeft: 0,
                paddingRight: 0
              },
            }}
          />
        </Box>
      )}

      {/* Pagination outside DashboardContainer - My Reviews Tab */}
      {activeMainTab === 2 && myReviewsPagination.totalCount > 0 && (
        <Box
          sx={{
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: '#ffffff',
            position: 'sticky',
            bottom: 0,
            zIndex: 10
          }}
        >
          <TablePagination
            component="div"
            count={myReviewsPagination.totalCount}
            page={myReviewsPagination.page}
            onPageChange={myReviewsPagination.onPageChange}
            rowsPerPage={myReviewsPagination.rowsPerPage}
            rowsPerPageOptions={[]}
            showFirstButton={true}
            showLastButton={true}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count}`
            }
            sx={{
              '& .MuiTablePagination-toolbar': {
                minHeight: '48px',
                paddingLeft: 0,
                paddingRight: 0
              },
            }}
          />
        </Box>
      )}

      {/* Pagination outside DashboardContainer - To Review Tab */}
      {activeMainTab === 1 && toReviewPagination.totalCount > 0 && (
        <Box
          sx={{
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: '#ffffff',
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            px: 0,
            pb: 0
          }}
        >
          <TablePagination
            component="div"
            count={toReviewPagination.totalCount}
            page={toReviewPagination.page}
            onPageChange={toReviewPagination.onPageChange}
            rowsPerPage={toReviewPagination.rowsPerPage}
            rowsPerPageOptions={[]}
            showFirstButton={true}
            showLastButton={true}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{
              '& .MuiTablePagination-toolbar': {
                minHeight: '48px',
                paddingLeft: 0,
                paddingRight: 0
              },
              p: 0
            }}
          />
        </Box>
      )}
    </>
  )
}

export default MyTasks
